<template>
  <div v-if="file">
    <h1>Share uploads from file</h1>
    <v-alert :value="true" type="info"
      >This page allows you to email a download link for files that have been
      uploaded onto a file.</v-alert
    >
    <v-card>
      <v-card-text>
        <strong>Working on file:&#160;</strong>
        <FileZ :file="file" />
      </v-card-text>
    </v-card>
    <br />
    <v-card>
      <v-card-title class="headline grey lighten-2">Recipients</v-card-title>
      <v-card-text
        ><div
          v-if="!emailAddresses.length"
          style="font-size: 1.2em; color: red"
        >
          No email addresses set
        </div>
        <v-chip
          v-for="(emailAddress, ecounter) in emailAddresses"
          :key="`emailAddress${ecounter}`"
          color="green"
          text-color="white"
          >{{ `${emailAddress.personal} &lt;${emailAddress.address}&gt;`
          }}<a @click="chooseEmailAddress(emailAddress)"
            ><v-icon>delete</v-icon></a
          ></v-chip
        >
        <h4>Choose from email addresses linked on file:</h4>
        <v-chip
          v-for="(fileEmailAddress, femcounter) in fileEmailAddresses"
          :key="`fileEmailAddress${femcounter}`"
          @click="chooseEmailAddress(fileEmailAddress)"
          small
        >
          {{
            `${fileEmailAddress.personal} &lt;${fileEmailAddress.address}&gt;`
          }}
        </v-chip>
        <div class="ajaxForm">
          <h4>Enter a new email address</h4>
          <v-form ref="formCreateEmailAddress" v-model="valid" lazy-validation>
            <v-text-field
              label="Email address"
              v-model="address"
              :rules="emailRules"
            />
            <v-text-field
              label="Person's name"
              v-model="personal"
              :rules="valueRules"
            />
            <v-btn @click="createEmailAddress" :disabled="!address || !personal"
              ><v-icon>add</v-icon> Create</v-btn
            >
          </v-form>
        </div>
      </v-card-text>
    </v-card>
    <br />
    <v-card>
      <v-card-title class="headline grey lighten-2">Email body</v-card-title>
      <v-card-text
        ><EmailTemplates
          :tag="'upload_share'"
          :fileid="$route.params.id"
          :entities="null"
          @updated="updated"
        />
        <EmailSignature :fileId="$route.params.id" />
      </v-card-text>
    </v-card>
    <br />
    <v-card>
      <v-card-title class="headline grey lighten-2"
        >Select the files for which you want to allow access:</v-card-title
      >
      <v-card-text>
        <v-list-tile v-for="uploadFile in uploadFiles" :key="uploadFile.id">
          <v-list-tile-action>
            <v-checkbox v-model="uploadFileIds" :value="uploadFile.id" />
          </v-list-tile-action>
          <v-list-tile-content @click="toggleUploadFile(uploadFile)">
            <v-list-tile-title>
              <span v-if="uploadFile.uploadDescription">{{
                uploadFile.uploadDescription.body
              }}</span>
              <span v-if="!uploadFile.uploadDescription">{{
                uploadFile.uploadDescription.id
              }}</span>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-card-text>
    </v-card>
    <br />
    <v-card>
      <v-card-title class="headline grey lighten-2"
        >Choose when the download link expires:</v-card-title
      >
      <v-card-text>
        <v-radio-group v-model="expires" :mandatory="true">
          <v-radio
            v-for="expiry in expiries"
            :key="expiry.value"
            :value="expiry.value"
          >
            <template slot="label">{{ expiry.label }} </template>
          </v-radio>
        </v-radio-group>
      </v-card-text>
    </v-card>
    <br />
    <v-checkbox
      v-model="copy"
      :label="`Send a carbon copy of the email to ${currentEmailAddress.address}`"
      v-if="currentEmailAddress"
    ></v-checkbox>
    <div v-if="error" style="font-size: 1.2em; color: red">{{ error }}</div>
    <v-btn @click="uploadFileShare"><v-icon>share</v-icon> Share</v-btn>
  </div>
</template>

<script>
import EmailSignature from "@/components/layout/widgets/EmailSignature";
import EmailTemplates from "@/components/layout/widgets/EmailTemplates";
import FileZ from "@/components/link/FileZ";
import nbmlaw from "@/api/nbmlaw";
import emailValidator from "email-validator";
export default {
  name: "UploadFileShare",
  metaInfo() {
    return {
      title: "Share uploads",
      titleTemplate: "%s - File - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { EmailSignature, EmailTemplates, FileZ },
  data() {
    return {
      file: null,
      fileEmailAddresses: null,
      currentEmailAddress: null,
      uploadFiles: null,
      emailAddresses: [],
      address: null,
      personal: null,
      uploadFileIds: null,
      subject: null,
      body: null,
      expiries: [
        { value: 7, label: "7 days" },
        { value: 14, label: "14 days" },
        { value: 21, label: "21 days" }
      ],
      expires: 14,
      copy: true,
      error: null,
      valid: false,
      valueRules: [(v) => !!v || "Value is required"],
      emailRules: [
        (v) => {
          if (!v) {
            return "Email address is required";
          }
          return emailValidator.validate(v) || "Invalid e-mail.";
        }
      ]
    };
  },
  methods: {
    async fetchFile() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.id}`
      );
      // console.log(response.data);
      this.file = response.data;
    },
    async fetchFileEmailAddresses() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.id}/addressbooks/emailAddresses`
      );
      // console.log(response.data);
      this.fileEmailAddresses = response.data;
    },
    async fetchCurrentUserEmail() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.id}/emailAddress`
      );
      // console.log(response.data);
      this.currentEmailAddress = response.data;
    },
    async fetchUploadFiles() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.id}/uploadFiles`
      );
      // console.log(response.data);
      this.uploadFiles = response.data;
    },
    async uploadFileShare() {
      try {
        await nbmlaw.post(
          `/cors/admin/file/files/${this.$route.params.id}/uploadFileShares`,
          {
            emailAddresses: this.emailAddresses,
            subject: this.subject,
            body: this.body,
            uploadFileIds: this.uploadFileIds,
            expires: this.expires,
            copy: this.copy
          }
        );
        this.$router.push({
          name: "file",
          params: { id: this.$route.params.id }
        });
      } catch (err) {
        console.error(err);
        this.error = err.response.data;
      }
    },
    chooseEmailAddress(emailAddress) {
      this.emailAddresses = this.emailAddresses.some(
        (e) => e.address === emailAddress.address
      )
        ? this.emailAddresses.filter((e) => e.address != emailAddress.address)
        : [...this.emailAddresses, emailAddress];
    },
    createEmailAddress() {
      if (this.$refs.formCreateEmailAddress.validate()) {
        this.chooseEmailAddress({
          address: this.address,
          personal: this.personal
        });
        this.address = null;
        this.personal = null;
      }
    },
    toggleUploadFile(uploadFile) {
      this.uploadFileIds = this.uploadFileIds.includes(uploadFile.id)
        ? this.uploadFileIds.filter((u) => u != uploadFile.id)
        : [...this.uploadFileIds, uploadFile.id];
    },
    updated(e) {
      if (e) {
        // console.log(e);
        this.subject = e.subject;
        this.body = e.body;
      }
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.fetchFile();
        this.fetchFileEmailAddresses();
        this.fetchCurrentUserEmail();
        this.fetchUploadFiles();
      }
    },
    "$route.params.uploadFileId": {
      immediate: true,
      handler() {
        this.uploadFileIds = [Number(this.$route.params.uploadFileId)];
      }
    }
  }
};
</script>